import type { CatalogMenuSnapshot } from "../gateway/catalog-price-client";
import { posRuntimeDb, type CatalogLookupRecord } from "./pos-db";
import type { TrustedTerminalIdentity } from "./terminal-state";

function clean(value: string | null | undefined) {
  return (value ?? "").trim().toLocaleLowerCase("th").replace(/\s+/g, " ");
}

function tokens(...values: Array<string | null | undefined>) {
  const result = new Set<string>();
  for (const raw of values) {
    const value = clean(raw);
    if (!value) continue;
    result.add(value);
    for (const part of value.split(/[^\p{L}\p{N}._+-]+/u).filter(Boolean)) result.add(part);
  }
  return [...result];
}

function sourceScope(identity: TrustedTerminalIdentity) {
  return `org:${identity.organization_id}:branch:${identity.branch_id}:catalog`;
}

function lookupScope(identity: TrustedTerminalIdentity) {
  return `org:${identity.organization_id}:branch:${identity.branch_id}:node:${identity.node_id}:catalog-lookup`;
}

function sourceCursorKey(identity: TrustedTerminalIdentity) {
  return `catalog:cursor:${sourceScope(identity)}`;
}

function lookupBuildKey(identity: TrustedTerminalIdentity) {
  return `catalog-lookup:build:${lookupScope(identity)}`;
}

function sourceRevision(value: unknown) {
  if (!value || typeof value !== "object") return null;
  const revision = (value as { revision?: unknown }).revision;
  return typeof revision === "number" && Number.isSafeInteger(revision) && revision >= 0
    ? revision
    : null;
}

function lookupBuildState(value: unknown) {
  if (!value || typeof value !== "object") return null;
  const candidate = value as { sourceRevision?: unknown; rowCount?: unknown };
  if (
    typeof candidate.sourceRevision !== "number"
    || !Number.isSafeInteger(candidate.sourceRevision)
    || candidate.sourceRevision < 0
    || typeof candidate.rowCount !== "number"
    || !Number.isSafeInteger(candidate.rowCount)
    || candidate.rowCount < 0
  ) {
    return null;
  }
  return {
    sourceRevision: candidate.sourceRevision,
    rowCount: candidate.rowCount,
  };
}

export async function rebuildCatalogLookup(identity: TrustedTerminalIdentity) {
  const source = sourceScope(identity);
  const target = lookupScope(identity);
  const [sourceCursor, previousBuild, currentLookupCount] = await Promise.all([
    posRuntimeDb.runtimeMeta.get(sourceCursorKey(identity)),
    posRuntimeDb.runtimeMeta.get(lookupBuildKey(identity)),
    posRuntimeDb.catalogLookup.where("scopeKey").equals(target).count(),
  ]);
  const currentSourceRevision = sourceRevision(sourceCursor?.value);
  const built = lookupBuildState(previousBuild?.value);

  // Catalog lookup is a rebuildable local index. Do not tokenize, delete and bulk-write the
  // entire catalog again when the canonical Published Catalog revision has not changed.
  // Row-count verification keeps this safe after a local index purge/schema recovery while
  // leaving Journal/Outbox and Business SoT untouched.
  if (
    currentSourceRevision !== null
    && built?.sourceRevision === currentSourceRevision
    && built.rowCount === currentLookupCount
  ) {
    return;
  }

  const rows = await posRuntimeDb.projections.where("scopeKey").equals(source).toArray();
  const next: CatalogLookupRecord[] = [];

  for (const row of rows) {
    if (row.domain !== "CATALOG_MENU" || !row.payload || typeof row.payload !== "object") continue;
    const snapshot = row.payload as CatalogMenuSnapshot;
    const menu = snapshot.menu;
    if (!menu?.item_id || !menu.item_code || !menu.name) continue;
    const variants = snapshot.variants?.length ? snapshot.variants : [];
    const common = [menu.name, menu.item_code, menu.category_code, menu.category_name];

    if (!variants.length) {
      next.push({
        key: `${target}:menu:${menu.item_id}:variant:BASE`,
        scopeKey: target,
        menuItemId: menu.item_id,
        menuVariantId: null,
        itemCode: menu.item_code,
        variantCode: null,
        normalizedName: clean(menu.name),
        categoryCode: menu.category_code ?? null,
        searchTokens: tokens(...common),
        revision: Number(row.revision) || 0,
        updatedAt: row.updatedAt,
      });
      continue;
    }

    for (const variant of variants) {
      next.push({
        key: `${target}:menu:${menu.item_id}:variant:${variant.variant_id}`,
        scopeKey: target,
        menuItemId: menu.item_id,
        menuVariantId: variant.variant_id,
        itemCode: menu.item_code,
        variantCode: variant.variant_code || null,
        normalizedName: clean(`${menu.name} ${variant.variant_label || variant.variant_code}`),
        categoryCode: menu.category_code ?? null,
        searchTokens: tokens(...common, variant.variant_code, variant.variant_label, ...(variant.options ?? []).flatMap((option) => [option.option_code, option.option_name])),
        revision: Number(row.revision) || 0,
        updatedAt: row.updatedAt,
      });
    }
  }

  await posRuntimeDb.transaction("rw", posRuntimeDb.catalogLookup, posRuntimeDb.runtimeMeta, async () => {
    await posRuntimeDb.catalogLookup.where("scopeKey").equals(target).delete();
    if (next.length) await posRuntimeDb.catalogLookup.bulkPut(next);
    if (currentSourceRevision !== null) {
      await posRuntimeDb.runtimeMeta.put({
        key: lookupBuildKey(identity),
        scopeKey: target,
        value: { sourceRevision: currentSourceRevision, rowCount: next.length },
        updatedAt: new Date().toISOString(),
      });
    }
  });
}

export async function searchCatalogLookup(identity: TrustedTerminalIdentity, searchText: string, limit = 50) {
  const scopeKey = lookupScope(identity);
  const query = clean(searchText);
  if (!query) return posRuntimeDb.catalogLookup.where("scopeKey").equals(scopeKey).limit(limit).toArray();
  return posRuntimeDb.catalogLookup
    .where("searchTokens")
    .startsWithIgnoreCase(query)
    .and((row) => row.scopeKey === scopeKey)
    .limit(limit)
    .toArray();
}
